import {
  ERR_CATALOGUE_CODES,
  buildErrorMessageMap,
  defaultMessageForCode,
} from './err-catalogue.generated.js';
import { ApiError, type ApiErrorBody } from './envelope.js';

const ERROR_MESSAGES: Record<string, string> = {
  ...buildErrorMessageMap(),
  'ERR-identity.session-expired': 'Your session has expired. Please sign in again.',
  'ERR-identity.invalid-credentials': 'Email or password is incorrect.',
  'ERR-AUTH-0001': 'Email or password is incorrect.',
  'ERR-AUTH-0002': 'This account is locked. Try again later or reset your password.',
  'ERR-AUTH-0003': 'This account is suspended. Contact support for help.',
  'ERR-AUTH-0010': 'Please sign in to continue.',
  'ERR-AUTH-0011': 'Your session has expired. Please sign in again.',
  'ERR-AUTH-0012': 'Your session is invalid. Please sign in again.',
  'ERR-AUTH-0013': 'Please verify your email before continuing.',
  'ERR-AUTH-0014': 'Your permissions changed. Please sign in again.',
  'ERR-AUTH-0021': 'Your session was ended for security. Please sign in again.',
  'ERR-AUTH-0022': 'Your session was revoked. Please sign in again.',
  'ERR-AUTH-0023': 'This device is not recognised. Please sign in again.',
  'ERR-AUTH-0031': 'Enter your authenticator code to finish signing in.',
  'ERR-AUTH-0032': 'That authenticator code is not correct.',
  'ERR-AUTH-0033': 'That verification code is not correct.',
  'ERR-AUTH-0034': 'This code has expired. Request a new one.',
  'ERR-AUTH-0035': 'This security method is already set up on your account.',
  'ERR-AUTH-0036': 'You must keep at least one security method on your account.',
  'ERR-AUTH-0041': 'Sign-in is not allowed from this source.',
  'ERR-AUTH-0042': 'You do not have access to that area.',
  'ERR-AUTH-0043': 'Your password has expired. Reset it to continue.',
  'ERR-AUTH-0051': 'Extra confirmation is required for this action.',
  'ERR-AUTH-0061': 'That sign-in method is not allowed.',
  'ERR-AUTH-0062': 'That PIN is not correct.',
  'ERR-AUTH-0063': 'PIN entry is locked. Try again later.',
  'ERR-AUTH-0064': 'This device session is no longer valid. Please sign in again.',
  'ERR-AUTH-0071': 'You do not have permission to perform this action.',
  'ERR-AUTH-0072': 'You do not have access to that scope.',
  'ERR-AUTH-0073': 'This action is not available on this surface.',
  'ERR-auth.forbidden': 'You do not have permission to perform this action.',
  'ERR-validation.failed': 'Please check the form and try again.',
  'ERR-CTLG-3004': 'Move or remove the products in this category first.',
  'ERR-order.not-found': 'Order not found.',
  'ERR-network.offline': 'You appear to be offline. Check your connection.',
};

export function mapErrorCode(code: string, fallbackMessage?: string): string {
  const apiMessage = fallbackMessage?.trim();
  const mapped = ERROR_MESSAGES[code];
  const isGeneratedPlaceholder = !mapped || mapped === defaultMessageForCode(code);

  // Prefer a real API/user-facing message when the backend sent one.
  if (
    apiMessage &&
    apiMessage !== code &&
    !/^an error occurred \(/i.test(apiMessage) &&
    apiMessage !== 'Something went wrong. Please try again.'
  ) {
    return apiMessage;
  }

  if (mapped && !isGeneratedPlaceholder) return mapped;
  return apiMessage || mapped || 'Something went wrong. Please try again.';
}

export function mapApiError(error: ApiErrorBody, fallbackMessage?: string): string {
  return mapErrorCode(error.code, fallbackMessage ?? error.message);
}

export function mapThrownError(error: unknown): string {
  if (error instanceof ApiError) {
    return mapErrorCode(error.code, error.message);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return mapErrorCode('ERR-UNKNOWN');
}

export function registerErrorMessages(catalogue: Record<string, string>): void {
  Object.assign(ERROR_MESSAGES, catalogue);
}

/**
 * Extracts per-field error messages from an ApiError or a raw error envelope.
 * Returns a map of field path -> human-readable message (10-07 contract).
 */
export function parseFieldErrors(thrown: unknown): Record<string, string> {
  const errors: Record<string, string> = {};
  if (thrown instanceof ApiError && thrown.details.length > 0) {
    for (const detail of thrown.details) {
      if (detail.field && detail.message && !errors[detail.field]) {
        errors[detail.field] = detail.message;
      }
    }
    return errors;
  }
  const raw = thrown as { error?: { details?: Array<{ field?: string; message?: string }> } };
  const details = raw?.error?.details;
  if (!Array.isArray(details)) return errors;
  for (const detail of details) {
    if (detail.field && detail.message && !errors[detail.field]) {
      errors[detail.field] = detail.message;
    }
  }
  return errors;
}

export function hasErrorCatalogueEntry(code: string): boolean {
  return code in ERROR_MESSAGES || ERR_CATALOGUE_CODES.includes(code);
}

export { ERR_CATALOGUE_CODES };
